import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Badge } from '@components/atoms/badge';
import { Button } from '@components/atoms/button';
import { Loader } from '@components/atoms/loader';
import { CheckMarkIcon } from '@components/icons/check-mark-icon';
import { ErrorMarkIcon } from '@components/icons/error-mark-icon';
import { useQueryParam } from '@hooks/use-query';
import { BadgeStatusEnum, DocsVerificationStatusEnum } from '@models/enums';
import { useLazyGetVerificationByIdQuery } from '@modules/dashboard/api/verifications-api';
import type { FinishStepProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps/finish/types';
import {
    useGenerateZkProofMutation,
    useLazyGetVerificationByIdQuery as useLazyGetWidgetVerificationIdQuery,
} from '@modules/docs-verification-widget/api';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { RootState } from '@store/root-reducer';
import { getImageUrl } from '@utils/get-image-url';

import { PreviewButton } from './preview-button';
import { FinishStepContainer } from './styles';

const fetchDelay = 10000;

// eslint-disable-next-line complexity
export const FinishStep: React.FC<FinishStepProps> = ({ onFinish, getZkProof }) => {
    const { t } = useTranslation();
    const organizationId = useSelector(
        (state: RootState) => state.dashboard?.session?.organizationId,
    );
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const isWeb3 = useSelector((state: RootState) => state.docsVerification.sdkOptions?.isWeb3);
    const apiUrl = useSelector((state: RootState) => state.docsVerification.sdkOptions?.apiUrl);
    const verificationId = useSelector((state: RootState) => state.docsVerification.verificationId);
    const shortId = useSelector(
        (state: RootState) => state.docsVerification.sdkOptions?.id,
    ) as string;
    const [getVerificationById, { data }] = useLazyGetVerificationByIdQuery();

    const timer = useRef<ReturnType<typeof setTimeout>>();
    const [getVerificationByIdStatus, { data: verificationData }] =
        useLazyGetWidgetVerificationIdQuery();
    const [generateZkProof] = useGenerateZkProofMutation();
    const isFinished = useRef(false);
    const [timeToRedirect, setTimeToRedirect] = useState(5000);
    const queryParam = useQueryParam();
    const botName = queryParam.search.get('botName');
    const [isLoading, setIsLoading] = useState(false);
    const [zkProof, setZkProof] =
        useState<Parameters<NonNullable<FinishStepProps['getZkProof']>>[0]>();
    const [zkProofError, setZkProofError] = useState('');

    const isUseSDK = !organizationId;

    const redirectToLink = useCallback(() => {
        if (
            sdkOptions?.redirectUrl ||
            sdkOptions?.settings?.finish_screen_settings?.redirect_link
        ) {
            window.location.href =
                (sdkOptions.redirectUrl as string) ??
                sdkOptions?.settings?.finish_screen_settings?.redirect_link;
        }
    }, [sdkOptions?.settings?.finish_screen_settings?.redirect_link, sdkOptions?.redirectUrl]);

    const startRedirectTimer = useCallback(() => {
        let time = timeToRedirect;
        const checkTime = () => {
            if (time === 0) {
                redirectToLink();
            } else {
                time -= 1000;
                setTimeToRedirect(time);
            }
            setTimeout(() => {
                checkTime();
            }, 1000);
        };

        setTimeout(() => {
            checkTime();
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdkOptions]);

    useEffect(() => {
        const handleFinish = async () => {
            if (onFinish) {
                await onFinish();
            }
            if (
                isUseSDK &&
                ((sdkOptions?.redirectUrl &&
                    !sdkOptions?.settings?.finish_screen_settings?.enabled) ||
                    (sdkOptions?.settings?.finish_screen_settings?.enabled &&
                        sdkOptions?.settings?.finish_screen_settings?.redirect_link &&
                        sdkOptions?.settings?.finish_screen_settings?.redirect_warning))
            ) {
                startRedirectTimer();
            }
        };

        if (!isFinished.current) {
            isFinished.current = true;
            handleFinish();
        }
    }, [sdkOptions, onFinish, startRedirectTimer, isUseSDK]);

    useEffect(() => {
        // no need to generate zk proof if  verification is not web3
        if (isWeb3) {
            setIsLoading(true);
            getVerificationByIdStatus({
                id: shortId,
                url: apiUrl as string,
            });
            timer.current = setInterval(
                () =>
                    getVerificationByIdStatus({
                        id: shortId,
                        url: apiUrl as string,
                    }),
                10000,
            );
        }

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
            }
        };
    }, [apiUrl, getVerificationByIdStatus, getZkProof, isWeb3, shortId]);

    useEffect(() => {
        // verified = undefined - verificiation in progress
        if (verificationData?.verified != null) {
            if (timer.current) {
                clearInterval(timer.current);
            }
            // verified = true - verification verified
            if (verificationData?.verified) {
                generateZkProof({ url: apiUrl as string, body: { short_id: shortId } })
                    .unwrap()
                    .then((response) => {
                        getZkProof?.(response?.proof);
                        setZkProof(response?.proof);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        switch (error?.data?.error_level) {
                            case 'needs_reverification':
                                switch (error?.data?.message) {
                                    case 'Proof does not match the current profile':
                                        setZkProofError(
                                            t(
                                                'docVerificationWidget.zkProof.error.mismatchedProfile',
                                            ),
                                        );
                                        break;
                                    case 'Proof has expired':
                                        setZkProofError(
                                            t('docVerificationWidget.zkProof.error.expired'),
                                        );
                                        break;
                                    default:
                                        setZkProofError(
                                            t('docVerificationWidget.verificationError.subTitle'),
                                        );
                                }

                                break;
                            case 'issuance_restricted':
                                setZkProofError(
                                    t('docVerificationWidget.zkProof.error.alreadyUsed'),
                                );
                                break;
                            default:
                                setZkProofError(
                                    t('docVerificationWidget.verificationError.subTitle'),
                                );
                        }
                        setIsLoading(false);
                    });
                // verified = false - verification failed
            } else {
                setZkProofError(t('docVerificationWidget.verificationError.subTitle'));
                setIsLoading(false);
            }
        }
    }, [apiUrl, generateZkProof, getZkProof, verificationData?.verified, shortId, t]);

    useEffect(() => {
        if (verificationId && !isUseSDK) {
            getVerificationById({
                organizationId,
                verificationId,
            });
        }
    }, [getVerificationById, organizationId, verificationId, isUseSDK]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (
                data?.status === DocsVerificationStatusEnum.PENDING ||
                data?.status === DocsVerificationStatusEnum.IN_PROGRESS
            ) {
                if (!isUseSDK) {
                    getVerificationById({
                        organizationId,
                        verificationId,
                    });
                }
            } else {
                clearInterval(interval);
            }
        }, fetchDelay);

        return () => {
            clearInterval(interval);
        };
    }, [data?.status, getVerificationById, organizationId, verificationId, isUseSDK]);

    const actualIcon = useMemo(() => {
        switch (data?.status) {
            case DocsVerificationStatusEnum.VERIFIED:
                return <CheckMarkIcon />;
            case DocsVerificationStatusEnum.FAILED:
                return <ErrorMarkIcon />;
            default:
                return <CheckMarkIcon />;
        }
    }, [data]);

    const actualTitle = useMemo(() => {
        switch (data?.status) {
            case DocsVerificationStatusEnum.VERIFIED:
                return sdkOptions?.settings?.finish_screen_settings?.enabled
                    ? sdkOptions?.settings?.finish_screen_settings?.title
                    : t('common.thankYou');
            case DocsVerificationStatusEnum.FAILED:
                return t('docVerificationWidget.finish.title.failed');
            default:
                return sdkOptions?.settings?.finish_screen_settings?.enabled
                    ? sdkOptions?.settings?.finish_screen_settings?.title
                    : t('common.thankYou');
        }
    }, [
        data?.status,
        sdkOptions?.settings?.finish_screen_settings?.title,
        sdkOptions?.settings?.finish_screen_settings?.enabled,
        t,
    ]);

    const isTelegram = sdkOptions?.source === 'telegram';

    const zkProofBlock = (
        <React.Fragment>
            {isWeb3 && (
                <Stack direction='column' justifyContent='center' alignItems='center'>
                    {isLoading && (
                        <Box padding='26px'>
                            <Loader
                                text={t('docVerificationWidget.finish.verificationInProgress')}
                            />
                        </Box>
                    )}
                    {!isLoading &&
                        (verificationData?.verified && zkProof ? (
                            <Box
                                bgcolor='#f6f8fa'
                                padding='12px'
                                borderRadius='6px'
                                fontSize='10px'
                                height='400px'
                                overflow='auto auto'
                                whiteSpace='pre'
                                width='100%'
                            >
                                {JSON.stringify(zkProof, null, 2)}
                            </Box>
                        ) : (
                            zkProofError && (
                                <Badge
                                    text={zkProofError}
                                    size='s'
                                    status={BadgeStatusEnum.ERROR}
                                />
                            )
                        ))}
                </Stack>
            )}
        </React.Fragment>
    );

    return (
        <FinishStepContainer>
            {isUseSDK && (
                <React.Fragment>
                    {sdkOptions?.settings?.finish_screen_settings?.enabled ? (
                        <React.Fragment>
                            <div className='icon icon-success' style={{ textAlign: 'center' }}>
                                <img
                                    src={getImageUrl('/images/sdk/verification/success-icon.png')}
                                    alt='Success icon'
                                />
                            </div>
                            <div className='header'>
                                <h3 className='finish-title'>
                                    {sdkOptions?.settings?.finish_screen_settings.title}
                                </h3>
                                <p className='finish-description'>
                                    {sdkOptions?.settings?.finish_screen_settings.main_text}
                                </p>
                            </div>
                            {sdkOptions?.settings?.finish_screen_settings.redirect_link && (
                                <React.Fragment>
                                    {sdkOptions?.settings?.finish_screen_settings
                                        ?.redirect_warning && (
                                        <div className='finish-description'>
                                            {
                                                sdkOptions?.settings?.finish_screen_settings
                                                    ?.redirect_warning
                                            }
                                        </div>
                                        /*        commented out till V2 of Finish screen
                                        (
                                        <div
                                            className='finish-description'
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'docVerificationWidget.finish.description.youWillBeRedirected',
                                                    {
                                                        count: timeToRedirect / 1000,
                                                    },
                                                ),
                                            }}
                                        /> */
                                    )}

                                    {sdkOptions?.settings?.finish_screen_settings.cta && (
                                        <Button
                                            variant='primary'
                                            className='button-redirect'
                                            onClick={() => redirectToLink()}
                                            size='xl'
                                        >
                                            {sdkOptions?.settings?.finish_screen_settings.cta}{' '}
                                        </Button>
                                    )}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div className='icon icon-success' style={{ textAlign: 'center' }}>
                                <img
                                    src={getImageUrl('/images/sdk/verification/success-icon.png')}
                                    alt='Success icon'
                                />
                            </div>
                            <div className='header'>
                                <h3 className='finish-title'>
                                    <span>{t('common.thankYou')}</span>
                                    {'\n'}
                                    {!isTelegram && (
                                        <span className='alt-title'>
                                            {isWeb3
                                                ? t(
                                                      'docVerificationWidget.finish.subtitle.youUploadedAllDocuments',
                                                  )
                                                : t(
                                                      'docVerificationWidget.finish.title.youUploadedAllDocuments',
                                                  )}
                                        </span>
                                    )}
                                </h3>
                                {sdkOptions?.redirectUrl && (
                                    <React.Fragment>
                                        <div
                                            className='finish-description'
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'docVerificationWidget.finish.description.youWillBeRedirected',
                                                    {
                                                        count: timeToRedirect / 1000,
                                                    },
                                                ),
                                            }}
                                        />
                                        <Button
                                            variant='primary'
                                            className='button-redirect'
                                            onClick={() => redirectToLink()}
                                            size='xl'
                                        >
                                            {t('common.goBack')}
                                        </Button>
                                    </React.Fragment>
                                )}
                                {!sdkOptions?.redirectUrl && isTelegram && (
                                    <React.Fragment>
                                        <p className='finish-description'>
                                            {t('docVerificationWidget.finish.description.telegram')}
                                        </p>
                                        {!!botName && (
                                            <Button
                                                variant='primary'
                                                className='button-redirect'
                                                size='xl'
                                                to={`tg://resolve?domain=${botName}`}
                                            >
                                                {t('docVerificationWidget.finish.goToTelegram')}
                                            </Button>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </React.Fragment>
                    )}

                    {zkProofBlock}
                </React.Fragment>
            )}
            {!isUseSDK && (
                <React.Fragment>
                    <div className='icon'>{actualIcon}</div>
                    <div className='header'>
                        <h3 className='finish-title'>{actualTitle}</h3>
                        <p className='finish-description'>
                            {sdkOptions?.settings?.finish_screen_settings?.enabled
                                ? sdkOptions?.settings?.finish_screen_settings?.main_text
                                : t('docVerificationWidget.finish.title.youUploadedAllDocuments')}
                        </p>
                        <p className='finish-description'>
                            {!sdkOptions?.settings?.finish_screen_settings?.enabled &&
                                t('docVerificationWidget.finish.subtitle.youUploadedAllDocuments')}
                        </p>
                    </div>
                    {!isWeb3 && <PreviewButton />}
                    {zkProofBlock}
                </React.Fragment>
            )}
        </FinishStepContainer>
    );
};
